import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { MAX_PHOTOS } from "./PhotoStep";

export const usePhotoUploads = (slug) => {
  const [photos, setPhotos] = useState([]);
  const countRef = useRef(0);
  countRef.current = photos.length;

  const update = (id, patch) => setPhotos((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const uploadOne = useCallback(async (photo) => {
    try {
      const file = await compressImage(photo.file);
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/public/contractors/${slug}/photos`, form);
      update(photo.id, { status: "done", path: data.path });
    } catch (e) {
      update(photo.id, { status: "error" });
      toast.error(errorMessage(e, "A photo failed to upload. Remove it and try again."));
    }
  }, [slug]);

  const add = (files) => {
    const room = MAX_PHOTOS - countRef.current;
    const images = files.filter((f) => f.type.startsWith("image/") || /\.(heic|heif)$/i.test(f.name));
    if (images.length > room) toast.message(`You can add up to ${MAX_PHOTOS} photos.`);
    const next = images.slice(0, room).map((file) => ({
      id: crypto.randomUUID(), file, preview: URL.createObjectURL(file), status: "uploading", path: null,
    }));
    setPhotos((ps) => [...ps, ...next]);
    next.forEach(uploadOne);
  };

  const remove = (id) => setPhotos((ps) => ps.filter((p) => p.id !== id));
  const ready = photos.length > 0 && photos.every((p) => p.status === "done");
  return { photos, add, remove, ready, uploading: photos.some((p) => p.status === "uploading") };
};
